import React, { Fragment, useEffect, useState } from 'react';
import { CommentsSectionLoader } from '@confluence-classic/confluence-utils/entry-points/CommentsSectionLoader.ts';
import { ClassicEditorContextProviderLoader } from '@confluence-classic/confluence-fabric-editor/src/components/ClassicEditorContextProvider/index.js';

import { Box } from '@atlaskit/primitives/compiled';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { usePageContentId } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal.ts';
import { SpaceOverviewSwitcherLoader } from '@confluence/space-overview/entry-points/SpaceOverview.ts';
import { EmbeddedViewPageLoader } from '@confluence/embedded-confluence/entry-points/EmbeddedViewPage.ts';

import { ContentExtensionsProvider } from '../../ContentExtensionsProvider/index.ts';
import { LazyDwellTime } from '../lazyComponents.js';
import type { SpaceOverviewExperience } from '../../DwellTime/DwellTime.tsx';
import { CommentContextWrapper } from '../commentContextWrapper.tsx';

// Currently SSR renders SpaceOverviewLoader directly
// TODO: We should render everything on the server side to unify the route files

type EmbeddedSpaceOverviewProps = {
	spaceKey: string;
	contentId: string;
};

const EmbeddedSpaceOverview = ({ spaceKey, contentId }: EmbeddedSpaceOverviewProps) => {
	return (
		<Box padding="space.200">
			<EmbeddedViewPageLoader
				contentId={contentId}
				spaceKey={spaceKey}
				classicComments={CommentsSectionLoader}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
				showHeaderButtons={false}
			/>
		</Box>
	);
};

type SpaceOverviewProps = {
	spaceKey: string;
	onExperienceResolved: (experience: Exclude<SpaceOverviewExperience, 'unknown'>) => void;
};

const SpaceOverview = ({ spaceKey, onExperienceResolved }: SpaceOverviewProps) => {
	return (
		<SpaceOverviewSwitcherLoader spaceKey={spaceKey} onExperienceResolved={onExperienceResolved} />
	);
};

type SpaceOverviewRouteComponentProps = {
	spaceKey: string;
};

export const SpaceOverviewRouteComponent = ({ spaceKey }: SpaceOverviewRouteComponentProps) => {
	const [contextContentId] = usePageContentId();
	const [spaceOverviewExperience, setSpaceOverviewExperience] =
		useState<SpaceOverviewExperience>('unknown');
	const contentId: string = contextContentId ?? '';

	useEffect(() => {
		if (!contentId || !fg('cc-dlp-ensure-content-id-for-legacy-space-overview')) {
			return;
		}

		waitForGlobal(
			{
				listener: 'SpaceOverviewRouteComponent.tsx',
				globalProperty: 'AJS.Meta.set',
			},
			() => {
				window.AJS.Meta.set('content-id-alt', contentId);
			},
		);

		return () => {
			window.AJS?.Meta?.set?.('content-id-alt', undefined);
		};
	}, [contentId]);

	const component = isEmbeddedConfluence_DO_NOT_USE() ? (
		<EmbeddedSpaceOverview contentId={contentId} spaceKey={spaceKey} />
	) : (
		<SpaceOverview spaceKey={spaceKey} onExperienceResolved={setSpaceOverviewExperience} />
	);

	return (
		<Fragment>
			<ContentExtensionsProvider>
				<CommentContextWrapper>{component}</CommentContextWrapper>
			</ContentExtensionsProvider>
			<LazyDwellTime attributes={{ spaceOverviewExperience }} />
		</Fragment>
	);
};
export default SpaceOverviewRouteComponent;
